import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {Section} from 'constants/NavConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {CrumbLink, RootCrumb, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import SidepanelListingSettingsDropdown from 'nav/sidepanel/header/SidepanelListingSettingsDropdown';
import CompactResultsPagerWrapper from 'nav/sidepanel/listing/CompactResultsPagerWrapper';
import ListingToolbarArea from 'nav/sidepanel/listing/ListingToolbarArea';
import * as SearchURLs from 'nav/sidepanel/listing/SearchURLs';
import SelectedRowsCounter from 'nav/sidepanel/listing/SelectedRowsCounter';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {ExpandedSidepanelSkeleton} from 'nav/sidepanel/SidepanelSkeleton';
import ToggleDropdown from 'nav/ToggleDropdown';
import BenchlingProfiler from 'performance/BenchlingProfiler';
import {composeSidepanelSearchResultsRenderedEvent} from 'performance/processSearchEvents';
import ListingFiltersContainer from 'search/filters/ListingFiltersContainer';
import QueryInput from 'search/QueryInput';
import {FilterKey, SearchType, SortKey} from 'search/SearchConstants';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as nyc from 'ui-library/NYC';
import ResultsPager from 'ui-library/ResultsPager';
import {BasicEvent} from 'util/Types';
import DeleteWorklistsButton from 'worklists/DeleteWorklistsButton';
import ScanIntoWorklistButton from 'worklists/ScanIntoWorklistButton';
import ExpandedWorklistsTable from 'worklists/sidepanel-components/ExpandedWorklistsTable';
import WorklistsSidepanelList from 'worklists/sidepanel-components/WorklistsSidepanelList';
import WorklistCreateButton from 'worklists/WorklistCreateButton';
import {getDefaultWorklistSearchParams} from 'worklists/WorklistSearchParams';

import {WorklistSummaryGraphQL} from '../../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';
import {
  WorklistsSidepanelQuery,
  WorklistsSidepanelQueryVariables,
} from './__generated/WorklistsSidepanelQuery';

export interface WorklistsSidepanelProps {
  isExpanded: boolean;
  isExpanding: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  onOpenWorklist: (worklistSummary: WorklistSummaryGraphQL) => void;
}

export const WORKLISTS_SIDEPANEL_QUERY = gql`
  query WorklistsSidepanelQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: WORKLISTS) {
    worklists(ids: $ids) {
      ...WorklistSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('WorklistSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * This component renders a sidepanel showing all worklists.
 */
export function WorklistsSidepanel(props: WorklistsSidepanelProps & SearchProps): JSX.Element {
  const queryResult = useSkippableBenchlingQuery<WorklistsSidepanelQuery, WorklistsSidepanelQueryVariables>(
    WORKLISTS_SIDEPANEL_QUERY,
    {
      skip: !props.searchResults,
      variables: {ids: props.searchResults?.ids ?? []},
    }
  );

  let worklists: Array<WorklistSummaryGraphQL> | null = null;
  if (queryResult.type === QueryResultType.SUCCESS) {
    worklists = accessibleObjs(queryResult.data.worklists);
  }

  // This is the "Home / Worklists" location display that appears at the top of the panel.
  const crumbs = (
    <SplitBreadcrumbs
      crumbs={[
        <CrumbLink
          key="breadcrumbs-home"
          text="Home"
          onClick={(e: BasicEvent) => {
            e.preventDefault();
            SidepanelActions.openSection(Section.HOME);
          }}
        />,
        <RootCrumb key="breadcrumbs-worklists" text="Worklists" />,
      ]}
    />
  );

  // This is the settings button that appears below the search bar to the right.
  const settingsDropdown = (
    <SidepanelListingSettingsDropdown
      onSetSortOrder={props.onUpdateSortOrder}
      onSetPageSize={props.onUpdatePageSize}
      searchParams={props.searchParams}
      availableSortKeys={[SortKey.SORT_MODIFIED_AT_KEY, SortKey.SORT_NAME_KEY]}
      displaySettingsConfig={{
        isCompact: props.isCompactDisplay,
        setIsCompactDisplay: props.setIsCompactDisplay,
      }}
    />
  );

  // This is the filter by worklist type dropdown that appears just below the search bar to the left.
  const filtersContainer = (
    <ListingFiltersContainer
      primaryFilterOptions={[{filterKey: FilterKey.WORKLIST_TYPE}]}
      filters={props.searchParams.filters}
      selectedSchema={null}
      onChangeFilters={props.onUpdateFilters}
    />
  );

  // This wraps the entire top section above the display of the worklists themselves.
  const header = (
    <ListingHeader
      isExpanded={props.isExpanded}
      buttons={
        <div>
          <ToggleDropdown className="u-mini-spacing-right" expanded={props.isExpanded} />
          <ScanIntoWorklistButton className="u-mini-spacing-right" />
          <WorklistCreateButton
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            refreshOptions={{
              refreshWorklistItemsSearch: false,
              refreshWorklistsSearch: true,
              refreshRecentWorklists: false,
            }}
          />
        </div>
      }
      queryInput={<QueryInput onUpdateQuery={props.onUpdateQueryString} query={props.searchParams.query} />}
      settingsDropdown={settingsDropdown}
      filtersContainer={filtersContainer}
      crumbs={crumbs}
      showShadow={true}
    />
  );

  const pagination = {
    offset: props.searchParams.offset,
    limit: props.searchParams.limit,
    total: props.searchResults ? props.searchResults.total : 0,
    isExact: props.searchResults ? props.searchResults.isExact : true,
  };

  const SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT = composeSidepanelSearchResultsRenderedEvent();

  // When expanded, we show the header plus a table with detailed info about each worklist,
  // and the ability to select worklists.
  if (props.isExpanded) {
    return (
      <ExpandedSidepanelSkeleton
        header={header}
        content={
          <>
            <div className="u-lr-split">
              <div className="u-flex-display">
                <ResultsPager
                  setOffset={props.onUpdateOffset}
                  isLoading={!props.searchResults}
                  pagination={pagination}
                  subtext={null}
                />
                {settingsDropdown}
              </div>
              <ListingToolbarArea>
                <SelectedRowsCounter numSelected={props.selectedIds.length} />
                <div className="buttonToolbar">
                  <div className="buttonGroup">
                    <DeleteWorklistsButton
                      selectedWorklists={
                        worklists ? worklists.filter((w) => props.selectedIds.includes(w.id)) : []
                      }
                      refreshOptions={{refreshRecentWorklists: true, refreshWorklistsSearch: true}}
                    />
                    <ExportAuditLogButton
                      buttonStyle={ButtonStyle.TERTIARY_WHITE}
                      ids={props.selectedIds ?? []}
                    />
                  </div>
                </div>
              </ListingToolbarArea>
            </div>
            <div className="u-flex-grow u-flex-display u-justify-content-center u-small-spacing-top">
              <BenchlingProfiler {...SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT}>
                <ExpandedWorklistsTable
                  worklists={worklists}
                  isCompact={props.isCompactDisplay}
                  selectedWorklistIds={props.selectedIds}
                  onOpenWorklist={props.onOpenWorklist}
                  onToggleSelectedWorklistIds={(ids: Array<string>, isSelected: boolean) => {
                    const newIds = isSelected
                      ? [...props.selectedIds, ...ids]
                      : // eslint-disable-next-line no-restricted-syntax
                        props.selectedIds.filter((id) => !_.contains(ids, id));
                    props.onSelectIds(newIds);
                  }}
                  setSort={(key, reverse) => {
                    props.onUpdateSortOrder([{key, reverse}]);
                  }}
                  sorts={props.searchParams.sorts}
                />
              </BenchlingProfiler>
            </div>
          </>
        }
        dataTestAttrs={{component: 'WorklistSidepanel', element: 'root', key: 'expanded'}}
      />
    );
  }

  // When collapsed, we show the header plus condensed info about each worklist.
  return (
    <nyc.Container
      className="sidepanel"
      size="sidepanel"
      dataTestAttrs={{component: 'WorklistSidepanel', element: 'root', key: 'collapsed'}}
    >
      <nyc.Row>{header}</nyc.Row>
      <nyc.Row fluid={true} className="u-vertical-flex">
        <div className="u-flex-grow u-position-relative">
          <div className="u-absolute-scroll-container">
            <CompactResultsPagerWrapper pagination={pagination} setOffset={props.onUpdateOffset}>
              <BenchlingProfiler {...SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT}>
                <WorklistsSidepanelList
                  onOpenWorklist={props.onOpenWorklist}
                  worklists={worklists}
                  isCompact={props.isCompactDisplay}
                />
              </BenchlingProfiler>
            </CompactResultsPagerWrapper>
          </div>
        </div>
      </nyc.Row>
    </nyc.Container>
  );
}

function getWorklistsURL(
  prevProps: WorklistsSidepanelProps & SearchProps,
  props: WorklistsSidepanelProps & SearchProps
): NavigationUpdate | null {
  if (!props.isExpanded) {
    return null;
  }
  return {url: SearchURLs.getNavigationURL('/worklists', props.searchParams, props.filterErrors)};
}

export default withSearching(WorklistsSidepanel, SearchType.WORKLIST, Section.WORKLISTS, {
  defaultSearchParams: getDefaultWorklistSearchParams(),
  getNavigationUpdate: getWorklistsURL,
});
